import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { maintenanceService, machineService, technicianService } from '../services';
import { format } from 'date-fns';

interface MaintenanceLog {
  id: number;
  machine_id: number;
  machine_name?: string;
  maintenance_type: 'preventive' | 'corrective' | 'breakdown' | 'inspection';
  description: string;
  technician_id?: number;
  technician_name?: string;
  start_date: string;
  end_date?: string;
  cost?: number;
  downtime_hours?: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  notes?: string;
}

const Maintenance: React.FC = () => {
  const [maintenanceLogs, setMaintenanceLogs] = useState<MaintenanceLog[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<MaintenanceLog | null>(null);
  const [otherMachineName, setOtherMachineName] = useState('');
  const [formData, setFormData] = useState<Partial<MaintenanceLog>>({
    machine_id: 0,
    maintenance_type: 'preventive',
    description: '',
    technician_id: undefined,
    start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    end_date: '',
    cost: 0,
    downtime_hours: 0,
    status: 'scheduled',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logsRes, machinesRes, techniciansRes] = await Promise.all([
        maintenanceService.getAll(),
        machineService.getAll(),
        technicianService.getAll({ is_active: 1 }),
      ]);
      setMaintenanceLogs(logsRes.logs || []);
      setMachines(machinesRes.machines || []);
      setTechnicians(techniciansRes.technicians || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (log?: MaintenanceLog) => {
    if (log) {
      setSelectedLog(log);
      // Format dates for datetime-local input
      const formatForInput = (date: string | undefined) => {
        if (!date) return '';
        return format(new Date(date), "yyyy-MM-dd'T'HH:mm");
      };
      // Check if it was an "Other" machine entry
      const isOther = !log.machine_id && !!(log as any).other_machine_name;
      setOtherMachineName(isOther ? (log as any).other_machine_name : '');
      setFormData({
        ...log,
        machine_id: isOther ? -1 : log.machine_id,
        start_date: formatForInput(log.start_date),
        end_date: formatForInput(log.end_date),
      });
    } else {
      setSelectedLog(null);
      setOtherMachineName('');
      setFormData({
        machine_id: 0,
        maintenance_type: 'preventive',
        description: '',
        technician_id: undefined,
        start_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        end_date: '',
        cost: 0,
        downtime_hours: 0,
        status: 'scheduled',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLog(null);
    setOtherMachineName('');
  };

  const handleSave = async () => {
    try {
      const isOther = formData.machine_id === -1;
      const payload = {
        ...formData,
        machine_id: isOther ? null : formData.machine_id,
        other_machine_name: isOther ? otherMachineName.trim() : null,
      };
      if (selectedLog) {
        await maintenanceService.update(selectedLog.id, payload);
      } else {
        await maintenanceService.create(payload);
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Failed to save maintenance log:', error);
      alert('Failed to save maintenance log');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this maintenance log?')) {
      try {
        await maintenanceService.delete(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete maintenance log:', error);
        alert('Failed to delete maintenance log');
      }
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'preventive': return 'success';
      case 'corrective': return 'warning';
      case 'breakdown': return 'error';
      case 'inspection': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Maintenance Logs
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Maintenance Log
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date & Time</TableCell>
              <TableCell>Machine</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>User</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {maintenanceLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  {log.start_date ? format(new Date(log.start_date), 'MMM dd, yyyy HH:mm') : '-'}
                </TableCell>
                <TableCell>{log.machine_name || '-'}</TableCell>
                <TableCell>
                  <Chip label={log.maintenance_type} size="small" color={getTypeColor(log.maintenance_type)} />
                </TableCell>
                <TableCell>{log.description}</TableCell>
                <TableCell>{log.technician_name || '-'}</TableCell>
                <TableCell align="right">${Number(log.cost || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={log.status} size="small" color={getStatusColor(log.status)} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenDialog(log)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(log.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {maintenanceLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No maintenance logs found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedLog ? 'Edit Maintenance Log' : 'Add New Maintenance Log'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Machine"
                value={formData.machine_id}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setFormData({ ...formData, machine_id: val });
                  if (val !== -1) setOtherMachineName('');
                }}
                required
              >
                <MenuItem value={0}>Select Machine</MenuItem>
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    {machine.machine_name} ({machine.machine_code})
                  </MenuItem>
                ))}
                <MenuItem value={-1}>Other</MenuItem>
              </TextField>
              {formData.machine_id === -1 && (
                <TextField
                  fullWidth
                  label="Machine Name"
                  value={otherMachineName}
                  onChange={(e) => setOtherMachineName(e.target.value)}
                  required
                  placeholder="Enter machine name"
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Maintenance Type"
                value={formData.maintenance_type}
                onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value as any })}
                required
              >
                <MenuItem value="preventive">Preventive</MenuItem>
                <MenuItem value="corrective">Corrective</MenuItem>
                <MenuItem value="breakdown">Breakdown</MenuItem>
                <MenuItem value="inspection">Inspection</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="User"
                value={formData.technician_id || ''}
                onChange={(e) => setFormData({ ...formData, technician_id: e.target.value ? Number(e.target.value) : undefined })}
              >
                <MenuItem value="">None</MenuItem>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    {tech.name} ({tech.employee_code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date & Time"
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date & Time"
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                required
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Downtime Hours"
                type="number"
                value={formData.downtime_hours}
                onChange={(e) => setFormData({ ...formData, downtime_hours: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedLog ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Maintenance;
